import './FilterCategoryList.scss';

import { IconChevronRight, IconChevronUp } from '@hulu-react-style-components/icons';
import React, { useCallback, useEffect, useState } from 'react';

import type { FilterConfig } from '../../../../apis/graphql/types';
import { FilterCategory } from '../../../../apis/graphql/types';
import Pill, { PillColor } from '../../../../common/Pill/Pill';
import bem from '../../../../utils/bem';
import { titleCase } from '../../../../utils/formatting';

export type FilterCategoryListProps = {
  configs: FilterConfig[];
  isFiltered?: boolean;
  selectedCounts?: Record<string, number>;
  addedFilterIds?: ReadonlySet<string>;
  onAddFilter?: (id: string) => void;
  expandForFilterId?: string | null;
};

type CategoryGroup = {
  category: string;
  displayLabel: string;
  filters: FilterConfig[];
};

const UNCATEGORIZED_KEY = 'OTHER';
const PINNED_CATEGORY: string = FilterCategory.Alerts;

function groupByCategory(configs: FilterConfig[]): CategoryGroup[] {
  const groupMap = new Map<string, FilterConfig[]>();

  for (const config of configs) {
    const key = config.category ?? UNCATEGORIZED_KEY;
    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)!.push(config);
  }

  return Array.from(groupMap.entries())
    .map(([category, filters]) => ({
      category,
      displayLabel: titleCase(category),
      filters: [...filters].sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => {
      if (a.category === PINNED_CATEGORY) return -1;
      if (b.category === PINNED_CATEGORY) return 1;
      if (a.category === UNCATEGORIZED_KEY) return 1;
      if (b.category === UNCATEGORIZED_KEY) return -1;
      return a.displayLabel.localeCompare(b.displayLabel);
    });
}

const [block, element] = bem('filter-category-list');

const FilterCategoryList = ({
  configs,
  isFiltered = false,
  selectedCounts,
  addedFilterIds,
  onAddFilter,
  expandForFilterId,
}: FilterCategoryListProps): JSX.Element => {
  const groups = groupByCategory(configs);
  const initialExpanded = Object.fromEntries(groups.map((g) => [g.category, g.category === PINNED_CATEGORY]));
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(initialExpanded);

  useEffect(() => {
    if (!expandForFilterId) return;
    const group = groups.find((g) => g.filters.some((f) => f.id === expandForFilterId));
    if (group) {
      setExpandedCategories((prev) => ({ ...prev, [group.category]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandForFilterId]);

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  }, []);

  const handleAddFilter = useCallback(
    (id: string) => {
      if (!onAddFilter) return;
      if (addedFilterIds?.has(id)) return;
      onAddFilter(id);
    },
    [onAddFilter, addedFilterIds]
  );

  if (configs.length === 0) {
    return <div className={block()} />;
  }

  return (
    <div className={block()}>
      {groups.map(({ category, displayLabel, filters }) => {
        const isExpanded = isFiltered || !!expandedCategories[category];
        const categoryCount = filters.reduce((sum, f) => sum + (selectedCounts?.[f.id] ?? 0), 0);

        return (
          <div key={category} className={element('category')}>
            <button
              className={element('category-header')}
              onClick={() => toggleCategory(category)}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <IconChevronUp width="12px" height="12px" title="collapse" />
              ) : (
                <IconChevronRight width="12px" height="12px" title="expand" />
              )}
              <span className={element('category-label')}>{displayLabel}</span>
              {categoryCount > 0 && <Pill color={PillColor.BLUE} text={String(categoryCount)} />}
            </button>
            {isExpanded && (
              <ul className={element('filter-list')}>
                {filters.map((config) => {
                  const filterCount = selectedCounts?.[config.id] ?? 0;
                  const isAdded = !!addedFilterIds?.has(config.id);
                  return (
                    <li
                      key={config.id}
                      className={element('filter-item', isAdded ? 'added' : null)}
                      onClick={(): void => handleAddFilter(config.id)}
                      role={onAddFilter ? 'button' : undefined}
                      aria-pressed={onAddFilter ? isAdded : undefined}
                    >
                      <span className={element('filter-label')}>{config.label}</span>
                      {filterCount > 0 && <Pill color={PillColor.BLUE} text={String(filterCount)} />}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FilterCategoryList;
